import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import {
    UpdateVenueAgentProvisionedCourtsInputDto,
    VenueAgentDto,
} from "../../../models/court-recording/dto/venue-agent.dto";
import { GET_VENUE_AGENTS_QUERY_KEY } from "./useListVenueAgents";

/**
 * Replaces an agent's admin-set `provisionedCourts` — the tamper-proof scope
 * the API authorises every agent recording call against (never the
 * self-reported `courts` heartbeat field).
 *
 * Sending an EMPTY array clears the restriction back to FULL-ORG access; it
 * does not disable the agent. Callers must make that consequence explicit
 * before submitting an empty selection.
 */
export const useUpdateVenueAgentProvisionedCourts = (organizationId: string) => {
    const client = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ agentId, courts }: { agentId: string; courts: number[] }) =>
            client.fetchApi<VenueAgentDto, UpdateVenueAgentProvisionedCourtsInputDto>({
                endpoint: `court-recordings/admin/venue-agents/${agentId}/provisioned-courts?organizationId=${organizationId}`,
                method: 'PATCH',
                body: { courts },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: GET_VENUE_AGENTS_QUERY_KEY(organizationId),
            });
        },
    });
};
